import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import AuthModal from './auth/AuthModal';
import { Menu, X, Scale, User, Crown } from 'lucide-react';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalTab, setAuthModalTab] = useState('login');
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  // Check for reset_token in URL on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const resetToken = urlParams.get('reset_token');
    if (resetToken) {
      console.log('Reset token found in URL, opening login modal for password reset');
      setAuthModalTab('login');
      setIsAuthModalOpen(true);
    }
  }, []);

  const navItems = [
    { name: 'Home', href: '#home' },
    { name: 'How it Works', href: '#how-it-works' },
    { name: 'Issues', href: '#legal-issues' },
    { name: 'Pricing', href: '#pricing' },
    { name: 'Testimonials', href: '#testimonials' },
    { name: 'FAQ', href: '#faq' }
  ];

  const openAuthModal = (tab = 'login') => {
    setAuthModalTab(tab);
    setIsAuthModalOpen(true);
  };

  const handleLogout = () => {
    logout();
    setIsMenuOpen(false);
  };

  const handleNavigation = (item) => {
    if (item.href.startsWith('#')) {
      // Navigate to home first, then scroll to section
      if (window.location.pathname !== '/') {
        navigate('/');
        // Wait for navigation to complete, then scroll
        setTimeout(() => {
          const element = document.querySelector(item.href);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      } else {
        // Already on home page, just scroll
        const element = document.querySelector(item.href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    } else {
      // Direct navigation for other links
      navigate(item.href);
    }
    setIsMenuOpen(false);
  };

  const getPlanBadgeColor = (plan) => {
    switch (plan) {
      case 'free': return 'bg-gray-100 text-gray-700';
      case 'standard': return 'bg-yellow-100 text-yellow-700';
      case 'premium': return 'bg-red-100 text-red-700';
      case 'lifetime': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getPlanIcon = (plan) => {
    if (plan === 'premium' || plan === 'lifetime') {
      return <Crown className="w-3 h-3" />;
    }
    return null;
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center">
              <img 
                src="/equalizer-logo.png" 
                alt="TheEqualizer.AI Logo" 
                className="h-10 w-auto object-contain"
              />
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              {navItems.map((item) => (
                <button
                  key={item.name}
                  onClick={() => handleNavigation(item)}
                  className="text-gray-600 hover:text-gray-900 font-medium transition-colors duration-200 bg-transparent border-none cursor-pointer"
                >
                  {item.name}
                </button>
              ))}
            </nav>

            {/* Desktop CTA/User Menu */}
            <div className="hidden md:flex items-center space-x-4">
              {isAuthenticated ? (
                <>
                  {/* User Plan Badge */}
                  <Badge 
                    variant="outline" 
                    className={`text-xs ${getPlanBadgeColor(user?.subscription_plan)}`}
                  >
                    {getPlanIcon(user?.subscription_plan)}
                    <span className={getPlanIcon(user?.subscription_plan) ? 'ml-1' : ''}>
                      {user?.subscription_plan?.charAt(0).toUpperCase() + user?.subscription_plan?.slice(1)}
                    </span>
                  </Badge>

                  {/* Dashboard Button */}
                  <Button 
                    variant="outline"
                    onClick={() => navigate('/dashboard')}
                    className="flex items-center space-x-2"
                  >
                    <User className="w-4 h-4" />
                    <span>Dashboard</span>
                  </Button>

                  {/* Logout Button */}
                  <Button 
                    variant="ghost"
                    onClick={handleLogout}
                    className="text-gray-600 hover:text-gray-900"
                  >
                    Sign Out
                  </Button>
                </>
              ) : (
                <>
                  <Button 
                    variant="ghost"
                    onClick={() => navigate('/register')}
                    className="text-gray-600 hover:text-gray-900"
                  >
                    Register
                  </Button>
                  <Button 
                    onClick={() => openAuthModal('login')}
                    className="bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white px-6 py-2 rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    Login
                  </Button>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </Button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="md:hidden absolute top-16 left-0 right-0 bg-white border-b border-gray-200 shadow-lg">
              <nav className="flex flex-col space-y-1 p-4">
                {navItems.map((item) => (
                  <button
                    key={item.name}
                    onClick={() => handleNavigation(item)}
                    className="text-gray-600 hover:text-gray-900 font-medium py-2 transition-colors duration-200 bg-transparent border-none cursor-pointer text-left w-full"
                  >
                    {item.name}
                  </button>
                ))}
                
                <div className="pt-4 border-t border-gray-200 space-y-2">
                  {isAuthenticated ? (
                    <>
                      <div className="flex items-center justify-between py-2">
                        <span className="text-sm text-gray-600">Plan:</span>
                        <Badge 
                          variant="outline" 
                          className={`text-xs ${getPlanBadgeColor(user?.subscription_plan)}`}
                        >
                          {getPlanIcon(user?.subscription_plan)}
                          <span className={getPlanIcon(user?.subscription_plan) ? 'ml-1' : ''}>
                            {user?.subscription_plan?.charAt(0).toUpperCase() + user?.subscription_plan?.slice(1)}
                          </span>
                        </Badge>
                      </div>
                      <Button 
                        onClick={() => {
                          navigate('/dashboard');
                          setIsMenuOpen(false);
                        }}
                        className="w-full justify-start"
                        variant="outline"
                      >
                        <User className="w-4 h-4 mr-2" />
                        Dashboard
                      </Button>
                      <Button 
                        onClick={handleLogout}
                        className="w-full justify-start text-red-600 hover:text-red-700"
                        variant="ghost"
                      >
                        Sign Out
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button 
                        onClick={() => {
                          navigate('/register');
                          setIsMenuOpen(false);
                        }}
                        className="w-full"
                        variant="outline"
                      >
                        Register
                      </Button>
                      <Button 
                        onClick={() => {
                          openAuthModal('login');
                          setIsMenuOpen(false);
                        }}
                        className="w-full bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white font-medium transition-all duration-200"
                      >
                        Login
                      </Button>
                    </>
                  )}
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Auth Modal */}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        defaultTab={authModalTab}
      />
    </>
  );
};

export default Header;